import { Icon } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import { Address } from '~/components/ListingCard';
import { formatNumber } from '~/lib';
import {
  SavedPropertyItemType,
  SavedPropertyListingType,
} from '~/types/activitiesTypes';
import {
  getCompoundPropertyTypes,
  getSavedPropertyAddress,
  getSavedPropertyImage,
  getSavedPropertyListingRoute,
} from '~/modules/activities/pages/Favorites/utils';
import ListingMediaIndicator from '~activities/pages/Favorites/ListingMediaIndicator';
import ListingNote from '~activities/pages/Favorites/ListingNote';
import ActionMenu from '~activities/pages/Favorites/ActionMenu';

type CompoundCardProps = {
  item: SavedPropertyItemType;
  handelDeleteFavorite: () => void;
  handleOpenNoteModal: () => void;
  handleDeleteNote: () => void;
  isSheared?: boolean;
  setShareRoute: React.Dispatch<React.SetStateAction<string>>;
};

export default function CompoundCard({
  item,
  handelDeleteFavorite,
  handleOpenNoteModal,
  handleDeleteNote,
  isSheared,
  setShareRoute,
}: CompoundCardProps) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const listing: SavedPropertyListingType = item.listing;
  const compoundRoute = getSavedPropertyListingRoute(listing, locale);
  const compoundImage = getSavedPropertyImage(listing);
  const compoundAddress = getSavedPropertyAddress(listing);
  const propertyTypes = getCompoundPropertyTypes(listing, locale);

  return (
    <article className="relative flex flex-col md:flex-row md:min-h-[236px] rounded-[10px] border border-gray bg-white compound-card">
      <div
        className={`${locale === 'en' ? 'md:!rounded-tr-none' : 'md:!rounded-tl-none'} relative md:w-[284px] h-[192px] md:h-full bg-gray_light_1 rounded-ss-[10px] rounded-se-[10px] md:rounded-t-0 md:rounded-s-[10px]`}
      >
        <a
          href={compoundRoute}
          target="_blank"
          rel="noreferrer"
          className={`block h-full w-full overflow-hidden ${locale === 'en' ? 'md:!rounded-tr-none' : 'md:!rounded-tl-none'} rounded-ss-[10px] rounded-se-[10px] md:rounded-t-0 md:rounded-s-[10px]`}
        >
          {compoundImage ? (
            <img
              src={compoundImage}
              alt={listing.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-gray__dark_1">
              <Icon name="image-placeholder" size="56px" />
            </div>
          )}
        </a>

        <ListingMediaIndicator
          photos={listing.photos.length}
          videoURL={listing.video_url ?? ''}
          className="absolute bottom-1.5x start-1.5x"
        />
        {listing?.user?.logo?.thumbnails?.small && (
          <span className="absolute bottom-[-26px] md:bottom-[calc(100%-52px)] overflow-hidden end-2x md:end-1.5x block bg-white px-1.5x py-[6px] text-caption-2 text-gray__dark_1 w-[56px] h-[56px] md:w-[40px] md:h-[40px] rounded-full md:rounded-[8px] border border-gray">
            <img
              src={listing?.user?.logo?.thumbnails?.small ?? ''}
              className="absolute top-0 left-0 w-full h-full "
            />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-2x md:p-3x">
        <div className="flex flex-col gap-2x justify-between h-full">
          <div className="flex flex-col gap-2x md:pe-[112px]">
            <div className="flex flex-col gap-x">
              <a
                href={compoundRoute}
                target="_blank"
                rel="noreferrer"
                className="text-title-4 text-black break-words"
              >
                {listing.title}
              </a>
              {listing.compound_field?.min_price ? (
                <p className="flex items-center gap-x text-title-5 text-gray__dark_2 whitespace-nowrap">
                  <span>{t('startingFrom')}</span>
                  <span className="flex items-center gap-0.5x">
                    <span>
                      {formatNumber(listing.compound_field?.min_price)}
                    </span>
                    <span>{t('egp')}</span>
                  </span>
                </p>
              ) : (
                <p className="h-[24px]"></p>
              )}
            </div>

            <div className="flex flex-col gap-0.5x">
              {compoundAddress ? <Address address={compoundAddress} /> : null}
              {propertyTypes ? (
                <div className="flex items-center gap-x-2 max-w-full min-w-0">
                  <Icon name="property-type" size="24px" />
                  <p
                    className="flex-1 min-w-0 w-full truncate text-caption text-gray__dark_2"
                    title={propertyTypes}
                  >
                    {propertyTypes}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
          <ListingNote
            noteText={item.note || ''}
            handleOpenNoteModal={handleOpenNoteModal}
            isSheared={isSheared}
          />
        </div>
      </div>
      <ActionMenu
        handelDeleteFavorite={handelDeleteFavorite}
        handleDeleteNote={handleDeleteNote}
        isSheared={isSheared}
        listingData={item}
        setShareRoute={setShareRoute}
      />
    </article>
  );
}
